import { Box } from '@mui/material';
import { useFlag } from '@unleash/proxy-client-react';
import { UnitDetailTopbar } from 'components/layout/UnitDetailTopbar';
import { CHAT_DEFAULT_WIDTH } from 'features/assetControl/assetControl.constants';
import { shouldRenderOperatorChat } from 'features/assetControl/assetControl.utils';
import { OperatorChat } from 'features/assetControl/components/OperatorChat';
import useAuth from 'hooks/useAuth';
import { useBreakpoint } from 'hooks/useBreakpoint';
import useProject from 'hooks/useProject';
import { useTimerange } from 'hooks/useTimerange';
import useUnitsConnectionStatus from 'hooks/useUnitsConnectionStatus';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Outlet, generatePath, useLocation, useNavigate, useParams } from 'react-router';
import { determineUnitAvailableDateRange, getSortedByActivityUnits } from 'utils/units';

export const ProjectMonitoringLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { projectSlug, unitExternalIdentifier } = useParams();
  const { projectUnits: units, projectUnitInstances: instances, project } = useProject();
  const { user } = useAuth();
  const isChatEnabled = useFlag('chat');
  const [isChatSplit, setIsChatSplit] = useState(false);
  const { isMobile } = useBreakpoint();
  const [isOpen, setIsOpen] = useState(false);

  const splitPath = location.pathname.split('/');
  const postFix = splitPath[splitPath.length - 1];

  const availableRange = determineUnitAvailableDateRange(project, instances, unitExternalIdentifier);

  const { range, ...timerangeProps } = useTimerange(availableRange);

  function createOnChangeUnitFunction(pathPostFix) {
    function onChangeUnit(unit) {
      const newPath = generatePath('/projects/:projectSlug/monitoring/:unitExternalIdentifier' + `/${pathPostFix}`, {
        projectSlug,
        unitExternalIdentifier: unit.externalIdentifier,
      });
      navigate(newPath, { replace: true });
    }
    return onChangeUnit;
  }

  const selectedAsset = units.find((unit) => unit.externalIdentifier === unitExternalIdentifier)!;
  const isOnline = useUnitsConnectionStatus()?.[selectedAsset.externalIdentifier] ?? false;
  const sortedUnits = getSortedByActivityUnits(instances);

  const isChatVisible = shouldRenderOperatorChat(user?.role, selectedAsset, isChatEnabled);

  useEffect(() => {
    if (!isChatVisible) {
      setIsOpen(false);
      setIsChatSplit(false);
    }
  }, [selectedAsset, isChatEnabled, user?.role]);

  return (
    <>
      <Helmet>
        <title>{project?.name}</title>
      </Helmet>
      <Box
        sx={{
          transition: 'margin-right 0.3s',
          marginRight: !isMobile && isChatSplit ? `${CHAT_DEFAULT_WIDTH}px` : 0,
        }}
      >
        <UnitDetailTopbar
          unitSelectorProps={{
            units: sortedUnits,
            onChange: createOnChangeUnitFunction(postFix),
            selectedUnitExtId: unitExternalIdentifier,
          }}
          timerangePickerProps={{
            range,
            availableRange,
            ...timerangeProps,
          }}
          showRangePicker={!location.pathname.includes('asset')}
        />
        <Outlet context={{ selectedAsset, range, isOnline }} />
      </Box>

      {isChatVisible && (
        <OperatorChat
          unit={selectedAsset}
          isOnline={isOnline}
          isChatSplit={isChatSplit}
          setIsChatSplit={setIsChatSplit}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
        />
      )}
    </>
  );
};
